import { ScrollArea } from "@/components/ui/scroll-area"

export default function MarkdownGuide() {
  return (
    <ScrollArea className="h-[60vh] w-full rounded-md border p-4">
      <div className="space-y-6">
        <h2 className="text-2xl font-bold">Guía de Edición Markdown</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Utiliza la sintaxis Markdown para formatear tu contenido. Aquí tienes algunos ejemplos básicos:
        </p>

        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold">Encabezados</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Usa `#` para los encabezados (H1 a H6).</p>
            <div className="mt-2 rounded-md bg-gray-100 p-3 font-mono text-sm dark:bg-gray-800">
              <pre>
                <code>
                  # Encabezado 1{"\n"}## Encabezado 2{"\n"}### Encabezado 3
                </code>
              </pre>
            </div>
            <div className="mt-2 space-y-1">
              <h1 className="text-2xl font-bold">Encabezado 1</h1>
              <h2 className="text-xl font-semibold">Encabezado 2</h2>
              <h3 className="text-lg font-medium">Encabezado 3</h3>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold">Texto en Negrita e Itálica</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Usa `**` o `__` para negrita, y `*` o `_` para itálica.
            </p>
            <div className="mt-2 rounded-md bg-gray-100 p-3 font-mono text-sm dark:bg-gray-800">
              <pre>
                <code>
                  **Texto en negrita**{"\n"}*Texto en itálica*{"\n"}__Negrita con guiones bajos__{"\n"}_Itálica con
                  guiones bajos_
                </code>
              </pre>
            </div>
            <div className="mt-2">
              <p>
                <strong>Texto en negrita</strong>
              </p>
              <p>
                <em>Texto en itálica</em>
              </p>
              <p>
                <strong>Negrita con guiones bajos</strong>
              </p>
              <p>
                <em>Itálica con guiones bajos</em>
              </p>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold">Listas</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Usa `-` o `*` para listas desordenadas, y `1.` para listas ordenadas.
            </p>
            <div className="mt-2 rounded-md bg-gray-100 p-3 font-mono text-sm dark:bg-gray-800">
              <pre>
                <code>
                  - Elemento 1{"\n"}- Elemento 2{"\n"} - Sub-elemento{"\n"}1. Primer elemento{"\n"}2. Segundo elemento
                </code>
              </pre>
            </div>
            <div className="mt-2">
              <ul className="list-disc pl-5">
                <li>Elemento 1</li>
                <li>
                  Elemento 2
                  <ul className="list-circle pl-5">
                    <li>Sub-elemento</li>
                  </ul>
                </li>
              </ul>
              <ol className="list-decimal pl-5">
                <li>Primer elemento</li>
                <li>Segundo elemento</li>
              </ol>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold">Enlaces</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Usa `[Texto del enlace](URL)` para crear enlaces.
            </p>
            <div className="mt-2 rounded-md bg-gray-100 p-3 font-mono text-sm dark:bg-gray-800">
              <pre>
                <code>[Visita Google](https://www.google.com)</code>
              </pre>
            </div>
            <div className="mt-2">
              <a
                className="text-blue-500 hover:underline"
                href="https://www.google.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                Visita Google
              </a>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold">Imágenes</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Usa `![Texto alternativo](URL de la imagen)` para insertar imágenes.
            </p>
            <div className="mt-2 rounded-md bg-gray-100 p-3 font-mono text-sm dark:bg-gray-800">
              <pre>
                <code>![Placeholder](https://via.placeholder.com/150)</code>
              </pre>
            </div>
            <div className="mt-2">
              <img alt="Placeholder" className="max-w-[150px] rounded-md" src="/placeholder.svg?height=150&width=150" />
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold">Bloques de Código</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Usa tres comillas invertidas (\`\`\`) para bloques de código.
            </p>
            <div className="mt-2 rounded-md bg-gray-100 p-3 font-mono text-sm dark:bg-gray-800">
              <pre>
                <code>
                  \`\`\`javascript{"\n"}function hello() {"{"}
                  {"\n"} console.log("Hello, World!");{"\n"}
                  {"}"}
                  {"\n"}\`\`\`
                </code>
              </pre>
            </div>
            <div className="mt-2 rounded-md bg-gray-800 p-3 text-white">
              <pre>
                <code>
                  function hello() {"{"}
                  {"\n"} console.log("Hello, World!");{"\n"}
                  {"}"}
                </code>
              </pre>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold">Citas en Bloque</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Usa `&gt;` para citas en bloque.</p>
            <div className="mt-2 rounded-md bg-gray-100 p-3 font-mono text-sm dark:bg-gray-800">
              <pre>
                <code>&gt; Esta es una cita.</code>
              </pre>
            </div>
            <div className="mt-2 border-l-4 border-gray-300 pl-4 italic text-gray-700">
              <p>Esta es una cita.</p>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold">Línea Horizontal</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Usa `---` o `***` para una línea horizontal.</p>
            <div className="mt-2 rounded-md bg-gray-100 p-3 font-mono text-sm dark:bg-gray-800">
              <pre>
                <code>---</code>
              </pre>
            </div>
            <div className="mt-2 border-t border-gray-300 pt-2"></div>
          </div>

          <div>
            <h3 className="text-lg font-semibold">Tablas</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Crea tablas con guiones y barras verticales.</p>
            <div className="mt-2 rounded-md bg-gray-100 p-3 font-mono text-sm dark:bg-gray-800">
              <pre>
                <code>
                  | Encabezado 1 | Encabezado 2 |{"\n"}| :----------- | :-----------: |{"\n"}| Celda 1 | Celda 2 |
                </code>
              </pre>
            </div>
            <div className="mt-2 overflow-x-auto">
              <table className="w-full table-auto border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="px-4 py-2 text-left">Encabezado 1</th>
                    <th className="px-4 py-2 text-center">Encabezado 2</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="px-4 py-2">Celda 1</td>
                    <td className="px-4 py-2 text-center">Celda 2</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </ScrollArea>
  )
}
